/* script */
//
//
//

// Utils
import { debounce } from 'lodash';
import * as mobiscroll from '@mobiscroll/javascript';
import * as moment from 'moment-timezone';

const __vue_script__ = {
    name: 'BaseCalendar',

    props: {
        events: {
            type: Array,
            default: () => []
        },

        resources: {
            type: Array,
            default: () => []
        },

        calendarConfig: {
            type: Object,
            default: () => ({})
        },

        dragToMove: {
            type: Boolean,
            default: false
        },

        dragToResize: {
            type: Boolean,
            default: false
        },

        invalid: {
            type: Array,
            default: () => []
        },

        displayTimezone: {
            type: String,
            default: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
    },

    runtime: null,

    watch: {
        events() {
            this.updateEvents();
        },

        resources() {
            console.debug('[BaseCalendar] Resources changed, updating UI:', this.resources);
            requestAnimationFrame(() => this.runtime?.setOptions({ resources: this.resources }));
        },

        invalid() {
            console.debug('[BaseCalendar] Invalid changed, updating UI:', this.invalid);
            requestAnimationFrame(() => this.runtime?.setOptions({ invalid: this.invalid }));
        },

        calendarConfig: {
            immediate: true,
            handler(value) {
                if (Object.keys(value).length > 0 && this.runtime) {
                    console.debug('[BaseCalendar] Calendar config changed, updating UI:', this.calendarConfig);
                    requestAnimationFrame(() => this.runtime.setOptions(this.calendarConfig));
                }
            }
        }
    },

    created() {
        // We create a debounced function to update the events
        // because the scheduler can be updated multiple times in a short period
        // and there are performance concerns if we update it every time.
        this.updateEvents = debounce(() => {
            console.debug('[BaseCalendar] Events changed, updating UI:', this.events);
            requestAnimationFrame(() => this.runtime?.setEvents(this.events));
        }, 500);
    },

    mounted() {
        this.init();
        window.addEventListener('theme-changed', this.handler);
    },

    beforeDestroy() {
        window.removeEventListener('theme-changed', this.handler);
        this.runtime?.destroy();
    },

    methods: {
        init() {
            mobiscroll.momentTimezone.moment = moment;

            const config = {
                // clickToCreate: 'single',
                data: this.events,
                dragToMove: this.dragToMove,
                dragToResize: this.dragToResize,
                displayTimezone: this.displayTimezone,
                timezonePlugin: mobiscroll.momentTimezone,
                invalid: this.invalid,
                locale: this.$i18n.locale,
                resources: this.resources,
                themeVariant: this.$utils.uiTheme.getCurrentTheme(this.$cookies),

                // For an extensive explanation of the events, see https://mobiscroll.com/docs/javascript/eventcalendar/scheduler#events
                onCellClick: (...args) => this.$emit('cell-click', ...args),
                onCellDoubleClick: (...args) => this.$emit('cell-double-click', ...args),
                onCellRightClick: (...args) => this.$emit('cell-right-click', ...args),
                onDestroy: (...args) => this.$emit('destroy', ...args),
                onEventClick: (...args) => this.$emit('event-click', ...args),
                onEventCreate: (...args) => this.$emit('event-create', ...args),
                onEventCreated: (...args) => this.$emit('event-created', ...args),
                onEventCreateFailed: (...args) => this.$emit('event-create-failed', ...args),
                onEventDelete: (...args) => this.$emit('event-delete', ...args),
                onEventDeleted: (...args) => this.$emit('event-deleted', ...args),
                onEventDoubleClick: (...args) => this.$emit('event-double-click', ...args),
                onEventDragEnd: (...args) => this.$emit('event-drag-end', ...args),
                onEventDragEnter: (...args) => this.$emit('event-drag-enter', ...args),
                onEventDragLeave: (...args) => this.$emit('event-drag-leave', ...args),
                onEventDragStart: (...args) => this.$emit('event-drag-start', ...args),
                onEventHoverIn: (...args) => this.$emit('event-hover-in', ...args),
                onEventHoverOut: (...args) => this.$emit('event-hover-out', ...args),
                onEventRightClick: (...args) => this.$emit('event-right-click', ...args),
                onEventUpdate: (...args) => this.$emit('event-update', ...args),
                onEventUpdated: (...args) => this.$emit('event-updated', ...args),
                onEventUpdateFailed: (...args) => this.$emit('event-update-failed', ...args),
                onInit: (...args) => this.$emit('init', ...args),
                onPageChange: (...args) => this.$emit('page-change', ...args),
                onPageLoaded: (...args) => this.$emit('page-loaded', ...args),
                onPageLoading: (...args) => this.$emit('page-loading', ...args),
                onSelectedEventsChange: (...args) => this.$emit('selected-events-change', ...args),
                onSelectedDateChange: (...args) => {
                    // Mobiscroll selects the start time of the local day in locale timezone, which does not
                    // make sense as the calendar is displayed in displayTimezone, we replace it with
                    // the same date-time in the display timezone
                    // Example: 2024-10-10T00:00:00+02:00 becomes 2024-10-10T00:00:00-07:00 in America/Los_Angeles timezone
                    if (args[0]?.date) {
                        args[0].date = this.$utils.time.transformLocalTimeToTz(args[0].date, this.displayTimezone).toDate();
                    }
                    this.$emit('selected-date-change', ...args);
                },
            };

            this.runtime = mobiscroll.eventcalendar(this.$refs.calendar, config);
        },

        handler({ detail }) {
            console.debug('[BaseCalendar] Theme changed, updating UI:', detail.theme);
            this.runtime?.setOptions({ themeVariant: detail.theme });
        }
    }
};

/* template */
var __vue_render__ = function () {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c("div", { ref: "calendar", staticClass: "base-calendar" })
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "apps/shared/src/ui/components/calendars/BaseCalendar.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__